// Device detection utilities for PWA features

/**
 * Detects if the current device is a mobile device
 * Checks user agent, screen size, and touch support
 */
export const isMobileDevice = (): boolean => {
  // Check user agent for mobile indicators
  const userAgent = navigator.userAgent;
  const isMobileUA = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  
  // Check screen size as backup (typical mobile breakpoint)
  const isSmallScreen = window.innerWidth <= 768;
  
  // Check for touch support
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Return true if any mobile indicator is present
  return isMobileUA || (isSmallScreen && isTouchDevice);
};

/**
 * Detects if the device is iOS (iPhone, iPad, iPod)
 */
export const isIOS = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

/**
 * Detects if the device is Android
 */
export const isAndroid = (): boolean => {
  return /Android/.test(navigator.userAgent);
};

/**
 * Detects if the app is running in standalone mode (PWA installed)
 */
export const isStandalone = (): boolean => {
  // Check if app is running in standalone mode (already installed)
  const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
  const isWebAPK = navigator.userAgent.includes('wv');
  const isInWebAppiOS = (window.navigator as any).standalone === true;
  
  return isStandaloneMode || isWebAPK || isInWebAppiOS;
};

/**
 * Checks if PWA can be installed on this device
 */
export const canInstallPWA = (): boolean => {
  // PWA installation is primarily useful on mobile devices
  return isMobileDevice() && !isStandalone();
};

/**
 * Gets the current screen size category
 */
export const getScreenSize = (): 'mobile' | 'tablet' | 'desktop' => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

/**
 * Checks if device has touch support
 */
export const hasTouchSupport = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * Gets safe area insets for iOS devices with notches
 * Returns CSS safe area inset values
 */
export const getSafeAreaInsets = () => {
  // These are CSS custom properties that iOS Safari sets
  // They're available in CSS via env(safe-area-inset-*)
  return {
    top: getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-top') || '0px',
    right: getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-right') || '0px',
    bottom: getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom') || '0px',
    left: getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-left') || '0px',
  };
};








